import { forwardRef, type ReactNode, type Ref } from "react";

import { Box, Tooltip, MotionBox, BoxProps, Row } from "@hightouch/htui";

import { RouterLink } from "src/router";
import { useNavWidth } from "src/contexts/nav-width-context";

import { useActiveLink } from "./utils";
import { withPermission } from "src/components/permission";
import { NavItemText, navItemTransitions } from "./nav-item-text";

const navItemBg = {
  hover: "rgba(255, 255, 255, 0.40)",
  active: "white",
  activeHover: "white",
};

type NavItemProps = {
  isSub?: boolean;
  isNested?: boolean;
  isActive?: boolean;
  isActiveSection?: boolean;
  isHovered?: boolean;
  isDisabled?: boolean;
  hasCollapsedTooltip?: boolean;
  href?: string;
  activePaths?: string[];
  newTab?: boolean;
  icon?: React.ElementType;
  label?: string;
  // Overrides the accessible name (defaults to `label`); use to convey state
  // (e.g. unread) to screen readers without changing the visible label.
  ariaLabel?: string;
  rightElement?: ReactNode;
} & Pick<
  BoxProps,
  "children" | "onMouseOver" | "onMouseLeave" | "onFocus" | "onClick"
>;

export const NavItem = forwardRef<HTMLButtonElement, NavItemProps>(
  (
    {
      isSub,
      isNested,
      isActive,
      isActiveSection,
      isHovered,
      isDisabled,
      hasCollapsedTooltip = true,
      href,
      activePaths,
      newTab,
      icon: Icon,
      label,
      ariaLabel,
      rightElement,
      children,
      onClick,
      ...props
    },
    ref,
  ) => {
    const { isNavOpen } = useNavWidth();
    const isLinkActive = useActiveLink({ href, isSub, activePaths });
    const isButton = onClick && !href;
    const hasRightElement =
      rightElement !== undefined &&
      rightElement !== null &&
      rightElement !== false;
    const shouldShowRightElement = isNavOpen && hasRightElement;

    // Section headers should never have active styles
    const active =
      typeof isActiveSection !== "undefined"
        ? false
        : Boolean(typeof isActive === "undefined" ? isLinkActive : isActive);

    const standardColor =
      active || isActiveSection ? "primary.base" : "text.tertiary";
    const hoverColor =
      active || isActiveSection ? "primary.base" : "text.primary";

    const unNestedHoverStyles = isNested
      ? {}
      : {
          bg: active ? navItemBg.activeHover : navItemBg.hover,
          borderColor: "base.border",
          boxShadow: "buttonShadowLight",
        };

    const content = (
      <MotionBox
        layout="position"
        // MotionBox types its ref as a div; `as` renders a button when interactive.
        ref={ref as Ref<HTMLDivElement>}
        aria-label={isButton ? (ariaLabel ?? label) : undefined} // Aria-label is required for interactive elements
        as={isButton ? "button" : "div"}
        className="group"
        display="flex"
        alignItems="center"
        bg={active ? navItemBg.active : "transparent"}
        borderRadius="lg"
        borderWidth="hairline"
        borderColor={active ? "base.border" : "transparent"}
        boxShadow={active ? "buttonShadowLight" : "none"}
        color={isHovered ? hoverColor : standardColor}
        gap={2}
        height={8}
        justifyContent={isNavOpen ? "flex-start" : "center"}
        overflow="hidden"
        position="relative"
        px={2.5}
        py={1.5}
        pl={isNavOpen && isNested ? 9 : 2}
        width="100%"
        zIndex={1} // Ensure item overlays background hover
        {...navItemTransitions}
        _hover={{
          ...unNestedHoverStyles, // Nested items have their own hover styles with `BackgroundHover`. This way there is no overlap between transitions.
          color: hoverColor,
        }}
        _focusVisible={{
          outline: "none",
          border: "none",
          boxShadow: "outline",
        }}
        _focus={{
          outline: "none !important",
        }}
        onClick={onClick}
        {...props}
      >
        {Icon && (
          <Icon
            color="inherit"
            flexShrink={0}
            {...navItemTransitions}
            _groupHover={{ color: hoverColor }}
          />
        )}

        {label && shouldShowRightElement ? (
          <Row
            align="center"
            flex={1}
            gap={2}
            justify="space-between"
            minWidth={0}
          >
            <NavItemText>{label}</NavItemText>
            <Box flexShrink={0}>{rightElement}</Box>
          </Row>
        ) : label ? (
          <NavItemText>{label}</NavItemText>
        ) : (
          children
        )}
      </MotionBox>
    );

    const container = isDisabled ? (
      <Box cursor="not-allowed" sx={{ div: { _hover: { bg: "none" } } }}>
        {content}
      </Box>
    ) : href ? (
      <Box
        as={RouterLink}
        flex={1}
        to={href}
        preload="intent"
        aria-label={ariaLabel ?? label}
        borderRadius="sm"
        display="flex"
        _focusVisible={{ boxShadow: "outline" }}
        {...(newTab ? { target: "_blank", rel: "noreferrer" } : {})}
      >
        {content}
      </Box>
    ) : (
      content
    );

    if (isNested) {
      return (
        <Box
          pos="relative"
          onMouseOver={props.onMouseOver}
          onMouseLeave={props.onMouseLeave}
          onFocus={props.onFocus}
          pb={2} // Give illusion of spacing between items, without sacrificing hover state
          _last={{
            pb: 0,
            ".active-indicator, .hover-indicator": { bottom: 2 },
            ".background-hover": { bottom: 0 },
          }}
        >
          {isHovered && <HoverIndicator />}
          {active && <ActiveIndicator />}
          {isNavOpen && isHovered && <BackgroundHover />}
          {container}
        </Box>
      );
    }

    // Display tooltip if nav is collapsed and the item is not nested
    if (!isNavOpen && !isNested && hasCollapsedTooltip) {
      return (
        <Tooltip message={label} placement="right">
          <Box>{container}</Box>
        </Tooltip>
      );
    }

    return container;
  },
);

const indicatorStyles: BoxProps = {
  width: "2px",
  borderRadius: "full",
  pos: "absolute",
  left: "17px",
  top: 2,
  bottom: 4,
};

function HoverIndicator() {
  return (
    <MotionBox
      {...indicatorStyles}
      aria-hidden="true"
      layoutId="nav-hover-indicator"
      className="hover-indicator"
      bg="text.disabled"
      zIndex={10}
      transition={{
        default: { ease: "easeInOut" },
        layout: { duration: 0.2 },
      }}
    />
  );
}

function ActiveIndicator() {
  return (
    <Box
      {...indicatorStyles}
      aria-hidden="true"
      className="active-indicator"
      bg="primary.base"
      zIndex={20}
    />
  );
}

function BackgroundHover() {
  return (
    <MotionBox
      aria-hidden="true"
      layoutId="nav-background-hover"
      className="background-hover"
      pos="absolute"
      inset={0}
      bottom={2}
      bg={navItemBg.hover}
      boxShadow="buttonShadowLight"
      borderRadius="lg"
      borderColor="base.border"
      borderWidth="hairline"
      transition={{
        default: { ease: "easeInOut" },
        layout: { duration: 0.2 },
      }}
    />
  );
}

NavItem.displayName = "NavItem";

// biome-ignore lint/style/useComponentExportOnlyModules: HOC-wrapped component; must be defined here alongside NavItem to avoid a circular-import TDZ crash from nav-section.
export const PermissionedNavItem = withPermission(NavItem);
